/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createReleaseChannel, updateReleaseChannel, updateRuntimeConfig } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type ChannelDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function ChannelDialog({ actionDialogReturn, onNavigate: _onNavigate }: ChannelDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { confirm, toast } = useFeedback();
  const {
    activeOrganization,
    activeProject,
    organizationById,
    projectById,
    environmentById,
    setWorkspaceVersion,
    workspace,
  } = useWorkspace();

  const { runtimeConfigs } = workspace;

  if (!actionDialog || actionDialog.kind !== 'channel') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'channel' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const previousChannelName = actionDialog.lookupChannel.trim();
      const nextChannelName = previousChannelName;
      const payload = {
        organizationId: actionDialog.organizationId.trim(),
        projectId: actionDialog.projectId.trim(),
        tenantEnvironmentId: actionDialog.tenantEnvironmentId.trim(),
        lookupEnvironment: actionDialog.lookupEnvironment,
        lookupChannel: previousChannelName,
        nextEnvironment: actionDialog.lookupEnvironment,
        nextChannel: nextChannelName,
        bundleCdnHostOverride: actionDialog.bundleCdnHostOverride.trim() || undefined,
        isActive: actionDialog.isActive
      };
      if (actionDialog.channelId) {
        await updateReleaseChannel(
          session.accessToken,
          actionDialog.lookupEnvironment || actionDialog.environment,
          actionDialog.lookupChannel || actionDialog.channel.trim() || actionDialog.environment,
          payload
        );
      } else {
        await createReleaseChannel(session.accessToken, payload);
      }
      const channelWasRenamed = previousChannelName !== nextChannelName;
      if (channelWasRenamed && runtimeConfigs.length > 0) {
        const affectedRuntimeConfigs = runtimeConfigs.filter(
          (config) =>
            config.organizationId === actionDialog.organizationId.trim() &&
            (config.projectId || '') === actionDialog.projectId.trim() &&
            !config.otaChannelId &&
            (config.otaChannel || '').trim() === previousChannelName
        );
        if (affectedRuntimeConfigs.length > 0) {
          const shouldRetarget = await confirm({
            title: 'Update release configs?',
            message: `${affectedRuntimeConfigs.length} release config${affectedRuntimeConfigs.length === 1 ? '' : 's'} still point to "${previousChannelName}". Update them to "${nextChannelName}" now?`,
            confirmLabel: 'Update configs',
            cancelLabel: 'Leave as-is'
          });
          if (shouldRetarget) {
            await Promise.all(
              affectedRuntimeConfigs.map((config) =>
                updateRuntimeConfig(session.accessToken, config.runtimeConfigId, {
                  name: config.name,
                  organizationId: config.organizationId,
                  projectId: config.projectId,
                  tenantEnvironmentId: config.tenantEnvironmentId,
                  platform: config.platform,
                  nativeVersionRange: config.nativeVersionRange,
                  environment: config.environment,
                  expressApiHostname: config.expressApiHostname,
                  staticCdnPrefix: config.staticCdnPrefix,
                  otaChannel: nextChannelName,
                  analyticsEnabled: config.analyticsEnabled,
                  analyticsPageviewsEnabled: config.analyticsPageviewsEnabled,
                  posthogApiKey: config.posthogApiKey,
                  posthogHost: config.posthogHost,
                  otelEnabled: config.otelEnabled,
                  otelCollectorUrl: config.otelCollectorUrl,
                  priority: config.priority,
                  isActive: config.isActive
                })
              )
            );
          }
        }
      }
      setWorkspaceVersion((v) => v + 1);
      toast('success', actionDialog.channelId ? 'Lane updated' : 'Lane created');
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Lane</div>
            <h3>Lane settings</h3>
            <p>Adjust the app-scoped bundle host and lane status.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <div className="settingsSplit">
            <label className="settingsField">
              <span>Organization</span>
              <input
                value={
                  organizationById.get(actionDialog.organizationId)?.name ||
                  activeOrganization?.name ||
                  'Selected organization'
                }
                disabled
              />
            </label>
            <label className="settingsField">
              <span>Project</span>
              <input
                value={
                  projectById.get(actionDialog.projectId)?.name ||
                  activeProject?.name ||
                  'Selected project'
                }
                disabled
              />
            </label>
          </div>
          <label className="settingsField">
            <span>Environment scope</span>
            <input
              value={
                environmentById.get(actionDialog.tenantEnvironmentId)?.name ||
                environmentById.get(actionDialog.tenantEnvironmentId)?.slug ||
                'Selected environment'
              }
              disabled
            />
          </label>
          <label className="settingsField">
            <span>Generated environment</span>
            <input value={actionDialog.environment} disabled />
            <p className="fieldHint">This comes from the app store version and cannot be edited here.</p>
          </label>
          <label className="settingsField">
            <span>Generated lane</span>
            <input value={actionDialog.channel} disabled />
            <p className="fieldHint">Lane names are generated from the app store version.</p>
          </label>
          <label className="settingsField">
            <span>Bundle host override</span>
            <input
              value={actionDialog.bundleCdnHostOverride}
              onChange={(e) => setActionDialog({ ...actionDialog, bundleCdnHostOverride: e.target.value })}
              placeholder="https://cdn.dev.example.com"
            />
            <p className="fieldHint">Leave blank to keep the original bundle host.</p>
          </label>
          <label className="settingsField">
            <span>Active</span>
            <label className="onboardingChoice" style={{ textAlign: 'left' }}>
              <div className="onboardingChoiceTop">
                <strong>Enabled</strong>
                <input
                  type="checkbox"
                  checked={actionDialog.isActive}
                  onChange={(e) => setActionDialog({ ...actionDialog, isActive: e.target.checked })}
                />
              </div>
              <span>Enable this release lane.</span>
            </label>
          </label>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy}>
            {dialogBusy ? 'Saving…' : actionDialog.channelId ? 'Save lane' : 'Create lane'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { ChannelDialog };
